import { useCallback, useEffect, useMemo } from 'react';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAtom } from 'jotai';
import { GetInboxMessagesResponse, InboxMessageData } from '@/../../server/types/apiTypes';
import { InboxEntityType, InboxFeedType, InboxMessageType } from '@/../../shared/types/inboxTypes';
import { SeenStatusEnum } from '@/../../shared/types/seenStatus';
import { usePeopleYouMayKnowInfiniteQuery } from '@/hooks/usePeopleYouMayKnowInfiniteQuery';
import { useAppSelector } from '@/store/hooks';
import { PYMKInboxMessageAtom, localSeenStatusAtom, shouldContinueFetchingAtom } from '@/store/inboxMessageAtom';
import OnboardingProgressIndicator from '../OnboardingProgressIndicator';
import InfiniteScrollList from '../lib/InfiniteScrollList';
import InboxItem from './InboxItem';

async function fetchInboxNewMessages(page: number) {
  const TAKE = 20;
  const skip = page * TAKE;

  const response = await axios.get<GetInboxMessagesResponse>(
    `/api/inbox/getInboxMessages?take=${TAKE}&skip=${skip}&feedType=${InboxFeedType.NEW}`,
  );
  if (response.data.success && response.data.data) {
    const { total, list } = response.data.data;
    return {
      hasMore: total > TAKE + skip,
      list,
    };
  }
  return {
    hasMore: false,
    list: [],
  };
}

async function fetchInboxRecentMessages(page: number) {
  const TAKE = 20;
  const skip = page * TAKE;

  const response = await axios.get<GetInboxMessagesResponse>(
    `/api/inbox/getInboxMessages?take=${TAKE}&skip=${skip}&feedType=${InboxFeedType.RECENT}`,
  );
  if (response.data.success && response.data.data) {
    const { total, list } = response.data.data;
    return {
      hasMore: total > TAKE + skip,
      list,
    };
  }
  return {
    hasMore: false,
    list: [],
  };
}

const InboxV1 = () => {
  const authState = useAppSelector((state) => state.auth);

  const inboxNewMessagesQuery = useInfiniteQuery({
    queryKey: ['inboxNewMessages'],
    queryFn: ({ pageParam }) => fetchInboxNewMessages(pageParam),
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.hasMore) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const inboxRecentMessagesQuery = useInfiniteQuery({
    queryKey: ['inboxRecentMessages'],
    queryFn: ({ pageParam }) => fetchInboxRecentMessages(pageParam),
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.hasMore) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const [shouldContinueFetching, setShouldContinueFetching] = useAtom(shouldContinueFetchingAtom);
  const shouldPauseFetching = useCallback(
    (data?: InfiniteData<{ hasMore: boolean; list: InboxMessageData[] }>) => {
      if (!data || shouldContinueFetching) {
        return false;
      }

      const flattenedData = data?.pages.flatMap((page) => page.list) ?? [];
      if (
        new Date(flattenedData[flattenedData.length - 1].createdAt) < new Date(Date.now() - 1000 * 60 * 60 * 24 * 7)
      ) {
        return true;
      }
      return false;
    },
    [shouldContinueFetching],
  );
  const renderSeeMoreButton = useCallback(() => {
    if (!shouldContinueFetching) {
      return (
        <div
          className="text-white text-sm underline font-bold text-center cursor-pointer"
          onClick={() => setShouldContinueFetching(true)}
        >
          See more
        </div>
      );
    }
  }, [shouldContinueFetching]);

  const { data: PYMKData } = usePeopleYouMayKnowInfiniteQuery();
  const [PYMKInboxMessage, setPYMKInboxMessage] = useAtom(PYMKInboxMessageAtom);
  useEffect(() => {
    const allPYMKs = PYMKData?.pages[0].list;
    if (allPYMKs && allPYMKs.length > 0 && PYMKInboxMessage.length === 0) {
      const randomPYMKs = allPYMKs.length > 2 ? allPYMKs.sort(() => Math.random() - 0.5).slice(0, 2) : allPYMKs;
      setPYMKInboxMessage(randomPYMKs);
    }
  }, [PYMKData]);
  const selectInboxRecentMessages = useCallback(
    (flattenedData: InboxMessageData[]): InboxMessageData[] => {
      const recentSevenDayMessages = flattenedData.filter(
        (message) => new Date(message.createdAt) >= new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
      );

      const earlierMessages = flattenedData.filter(
        (message) => new Date(message.createdAt) < new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
      );

      const PYMK_MESSAGE: InboxMessageData = {
        id: 'PEOPLE_YOU_MAY_KNOW',
        userId: 'PEOPLE_YOU_MAY_KNOW',
        type: InboxMessageType.PEOPLE_YOU_MAY_KNOW,
        entityType: InboxEntityType.PEOPLE_YOU_MAY_KNOW,
        entityId: 'PEOPLE_YOU_MAY_KNOW',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
        updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7),
        seenStatus: SeenStatusEnum.SEEN,
      };

      if (shouldContinueFetching) {
        return [...recentSevenDayMessages, PYMK_MESSAGE, ...earlierMessages];
      }

      return [...recentSevenDayMessages, PYMK_MESSAGE];
    },
    [shouldContinueFetching],
  );

  const [localSeenStatus, setLocalSeenStatus] = useAtom(localSeenStatusAtom);
  const flattenedNewMessages = useMemo(() => {
    return inboxNewMessagesQuery.data?.pages.flatMap((page) => page.list) ?? [];
  }, [inboxNewMessagesQuery.data]);

  useEffect(() => {
    flattenedNewMessages.forEach((message) => {
      if (!(message.id in localSeenStatus)) {
        setLocalSeenStatus((prev) => ({ ...prev, [message.id]: true }));
      }
    });
  }, [flattenedNewMessages]);

  const handleInboxItemClick = useCallback(
    (messageId: string) => () => {
      setLocalSeenStatus((prev) => ({ ...prev, [messageId]: false }));
    },
    [setLocalSeenStatus],
  );

  return (
    <div>
      {!authState.isOnBoardingTutorialComplete && (
        <OnboardingProgressIndicator currentStep={authState.numNotesOrQuestionsAnswered} />
      )}
      {(inboxNewMessagesQuery.data?.pages?.[0].list.length ?? 0) > 0 && (
        <>
          <div className="text-white text-lg font-bold text-left px-6 mt-6">New</div>
          <InfiniteScrollList
            infiniteQuery={inboxNewMessagesQuery}
            renderItem={(message) => (
              <InboxItem {...message} isNew={localSeenStatus[message.id]} onClick={handleInboxItemClick(message.id)} />
            )}
            estimateSizeFn={(_) => 40}
            paddingEnd={0}
            gap={16}
            px={0}
          />
        </>
      )}
      {(inboxRecentMessagesQuery.data?.pages?.[0].list.length ?? 0) > 0 && (
        <>
          <div className="text-white text-lg font-bold text-left px-6 mt-6">Recent</div>
          <InfiniteScrollList
            infiniteQuery={inboxRecentMessagesQuery}
            renderItem={(message) => <InboxItem {...message} />}
            estimateSizeFn={() => 40}
            paddingEnd={100}
            gap={16}
            px={0}
            shouldPauseFetching={shouldPauseFetching}
            renderSeeMoreButton={renderSeeMoreButton}
            select={selectInboxRecentMessages}
          />
        </>
      )}
    </div>
  );
};

export default InboxV1;
